import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Workflow,
  Activity,
  Network,
  Cpu,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  Eye,
  Layers,
  Target,
  CheckCircle2,
  Sparkles,
  Factory,
  Truck,
  ShoppingBag,
  Briefcase,
  Zap,
  Building2,
  ShieldCheck,
  Settings2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const PAIN_POINTS = [
  { icon: AlertTriangle, label: "Medienbrüche & Doppelarbeiten" },
  { icon: Eye, label: "Fehlende Transparenz" },
  { icon: Settings2, label: "Unklare Verantwortlichkeiten" },
  { icon: RefreshCw, label: "Wartezeiten & lange Durchlaufzeiten" },
  { icon: Activity, label: "Nicht abgestimmte Abläufe" },
  { icon: BarChart3, label: "Steigende Fehlerquoten & Kosten" },
];

const SERVICES = [
  {
    icon: Workflow,
    title: "Prozessanalyse & Prozessmapping",
    text: "Wir erfassen Geschäftsprozesse, dokumentieren Abläufe und schaffen Transparenz über Schnittstellen, Verantwortlichkeiten und Abhängigkeiten.",
  },
  {
    icon: Activity,
    title: "Wertstrom- & Effizienzanalysen",
    text: "Wir analysieren Informations- und Materialflüsse, identifizieren Verschwendung, Wartezeiten und Engpässe – und erhöhen die Wertschöpfung gezielt.",
  },
  {
    icon: Network,
    title: "Organisations- & Strukturentwicklung",
    text: "Wir gestalten klare Verantwortlichkeiten, effiziente Entscheidungswege und belastbare Organisationsstrukturen für nachhaltige Skalierung.",
  },
  {
    icon: Cpu,
    title: "Digitalisierung von Prozessen",
    text: "Wir identifizieren passende digitale Werkzeuge und begleiten deren Einführung – damit Prozessmanagement und Digitalisierung zusammen wirken.",
  },
  {
    icon: BarChart3,
    title: "KPI- & Steuerungssysteme",
    text: "Wir entwickeln Kennzahlensysteme, Dashboards und Managementinstrumente zur kontinuierlichen Leistungsüberwachung und Steuerung.",
  },
  {
    icon: RefreshCw,
    title: "Kontinuierliche Verbesserung",
    text: "Wir verankern Verbesserungsmechanismen dauerhaft in der Organisation und bauen eine Kultur der kontinuierlichen Weiterentwicklung auf.",
  },
];

const APPROACH = [
  {
    icon: Eye,
    title: "Transparenz schaffen",
    text: "Wir betrachten Informationsflüsse, Materialflüsse, Entscheidungswege und Verantwortlichkeiten entlang der gesamten Wertschöpfungskette.",
  },
  {
    icon: Target,
    title: "Engpässe identifizieren",
    text: "Wir machen Reibungsverluste und nicht wertschöpfende Tätigkeiten sichtbar – objektiv, datenbasiert und ganzheitlich.",
  },
  {
    icon: Layers,
    title: "Zielstruktur entwickeln",
    text: "Gemeinsam definieren wir eine organisatorisch und technologisch tragfähige Zielstruktur – mit messbarem Nutzen.",
  },
  {
    icon: Sparkles,
    title: "Umsetzung begleiten",
    text: "Von der Konzeption bis zur operativen Verankerung – wir begleiten den Wandel bis zum nachweisbaren Ergebnis.",
  },
];

const INDUSTRIES = [
  { icon: Factory, label: "Industrie & Produktion" },
  { icon: Settings2, label: "Maschinen- & Anlagenbau" },
  { icon: Truck, label: "Logistik & Supply Chain" },
  { icon: ShoppingBag, label: "Handel & E-Commerce" },
  { icon: Briefcase, label: "Dienstleistungsunternehmen" },
  { icon: Zap, label: "Energie & Infrastruktur" },
  { icon: Building2, label: "Öffentliche Einrichtungen" },
  { icon: ShieldCheck, label: "Defense & Security" },
];

const FAILURE_REASONS = [
  "Prozesse werden isoliert betrachtet – ohne Auswirkungen auf angrenzende Bereiche",
  "Fehlende objektive Datengrundlage zur Ursachenanalyse",
  "Veränderungen werden ausschließlich organisatorisch geplant",
  "Mitarbeiter und Führungskräfte werden nicht ausreichend eingebunden",
];

export default function ProcessOptimization() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="process-optimization-page" className="font-body bg-white text-slate-900 min-h-screen">
      <SEO
        title="Prozessoptimierung & Strukturen | X10 Management"
        description="Effiziente Prozesse als Grundlage nachhaltigen Wachstums. X10 begleitet Unternehmen bei Prozessanalyse, Wertstromoptimierung, Organisations- und Digitalisierungsprojekten – von der Analyse bis zur operativen Umsetzung."
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0A192F] text-white pt-32 lg:pt-36">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#E91E63]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link
              to="/#services"
              data-testid="po-back"
              className="inline-flex w-fit items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={16} /> Zurück zu Leistungen
            </Link>
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#E91E63]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E91E63]" />
                Discipline 01 · Prozessoptimierung
              </span>
            </div>
            <h1 className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl">
              Effiziente Prozesse als
              <span className="block text-[#E91E63] mt-2">Grundlage nachhaltigen Wachstums.</span>
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-white/85 font-light leading-relaxed max-w-3xl">
              Prozessoptimierung ist weit mehr als Kostensenkung. Es geht darum, Strukturen zu schaffen, die Unternehmen
              leistungsfähiger, widerstandsfähiger und skalierbarer machen – mit messbarem Wirkungsgrad.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/#contact"
                data-testid="po-cta-contact"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#E91E63] hover:text-white transition-all duration-300"
              >
                Potenzial-Analyse starten
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#services"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Unsere Leistungen
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 max-w-md ml-auto">
              <img
                src="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1200&q=80"
                alt="Effiziente Prozesse und Industrieoptimierung"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/80 via-[#0A192F]/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#E91E63] font-semibold">Strategic Field</div>
                <div className="font-heading text-lg font-semibold mt-1 leading-tight">
                  Prozessoptimierung &amp; Strukturen
                </div>
                <div className="text-sm text-white/80 mt-1">
                  Lean · Wertstrom · Skalierbarkeit
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Der unterschätzte Hebel</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
            Während andere Vertrieb skalieren, optimieren die Besten ihre Prozesse.
          </h2>
          <div className="mt-8 space-y-6 text-lg text-slate-700 leading-relaxed">
            <p>
              Viele Unternehmen investieren erhebliche Ressourcen in Vertrieb, Marketing oder neue Technologien – und
              übersehen dabei einen der größten Hebel für wirtschaftlichen Erfolg: <span className="text-[#0A192F] font-medium">die eigenen Prozesse</span>.
            </p>
            <p>
              Moderne Ansätze wie Lean Management, Wertstromanalyse und datenbasierte Prozesssteuerung konzentrieren
              sich darauf, Verschwendung zu reduzieren, Engpässe sichtbar zu machen und wertschöpfende Tätigkeiten
              konsequent in den Mittelpunkt zu stellen.
            </p>
          </div>
        </div>
      </section>

      {/* Pain Points Grid */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">
              Wo Wertschöpfung verloren geht
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Sechs typische Quellen versteckter Kosten.
            </h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              In zahlreichen Unternehmen entstehen täglich unnötige Kosten – durch Reibungsverluste, fehlende
              Transparenz und nicht abgestimmte Abläufe zwischen Abteilungen:
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PAIN_POINTS.map(({ icon: Icon, label }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-slate-200 hover:border-[#E91E63]/50 hover:shadow-[0_0_24px_rgba(233,30,99,0.12)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-[#E91E63]/10 border border-[#E91E63]/30 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-[#E91E63]" />
                </div>
                <div className="font-heading text-base font-semibold text-[#0A192F]">{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why projects fail (dark) */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[440px] h-[440px] rounded-full bg-[#E91E63]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#E91E63]">
              <AlertTriangle size={14} /> Warum Projekte scheitern
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              Optimierung scheitert selten an Ideen.
              <span className="text-[#E91E63]"> Sie scheitert an der Umsetzung.</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Nachhaltige Prozessverbesserung entsteht nur, wenn Strategie, Organisation, Menschen und Technologie
              gemeinsam betrachtet werden. Genau hier setzt unser Beratungsansatz an.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {FAILURE_REASONS.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
                >
                  <CheckCircle2 size={18} className="text-[#E91E63] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-200 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* X10 Approach */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Unser Ansatz</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Nicht aus Sicht einzelner Abteilungen, sondern entlang der gesamten Wertschöpfungskette.
            </h2>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">
              Wir schaffen Transparenz über bestehende Abläufe, machen Potenziale sichtbar und entwickeln gemeinsam
              eine Zielstruktur, die organisatorisch wie technologisch tragfähig ist – mit messbarem Nutzen statt
              theoretischer Konzepte.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {APPROACH.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-6 rounded-2xl bg-[#F3F4F6] border border-slate-200 hover:border-[#1E3A8A]/40 hover:shadow-md transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-[#1E3A8A]/10 border border-[#1E3A8A]/30 flex items-center justify-center">
                  <Icon size={18} className="text-[#1E3A8A]" />
                </div>
                <div className="mt-4 font-heading text-lg font-semibold text-[#0A192F]">
                  <span className="text-[#E91E63] mr-1">0{i + 1}.</span>
                  {title}
                </div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-[#F3F4F6] py-16 lg:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Unsere Leistungen</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Sechs Bausteine für nachhaltige Prozessexzellenz.
            </h2>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">
              Von der Analyse bis zur operativen Umsetzung – wir kombinieren methodische Tiefe mit branchen­spezifischer
              Erfahrung. Jedes Modul ist einzeln einsetzbar und entfaltet im Zusammenspiel die volle Wirkung.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group p-6 rounded-2xl bg-[#0A192F] text-white border border-[#0A192F] hover:border-[#E91E63]/50 hover:shadow-[0_0_30px_rgba(233,30,99,0.2)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-white/10 border border-[#E91E63]/40 flex items-center justify-center">
                  <Icon size={18} className="text-[#E91E63]" />
                </div>
                <div className="mt-4 font-heading text-lg font-semibold leading-tight">{title}</div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Branchen</span>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
                Branchen­übergreifend einsetzbar.
              </h2>
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                Unsere Methoden lassen sich in nahezu allen Unternehmensbereichen anwenden – gerade in Zeiten
                steigender Kosten, volatiler Lieferketten und wachsender regulatorischer Anforderungen werden
                effiziente Prozesse zum entscheidenden Wettbewerbsfaktor.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
                {INDUSTRIES.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 p-4 rounded-xl bg-[#F3F4F6] border border-slate-200 hover:border-[#E91E63]/40 transition-colors duration-300"
                  >
                    <div className="w-10 h-10 rounded-full bg-white border border-[#E91E63]/30 flex items-center justify-center flex-shrink-0">
                      <Icon size={16} className="text-[#0A192F]" />
                    </div>
                    <span className="text-sm font-medium text-[#0A192F]">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Line */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative p-8 lg:p-12 rounded-3xl bg-white border border-slate-200 shadow-sm">
            <div className="absolute -top-4 left-8 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A192F] text-[10px] uppercase tracking-[0.3em] font-extrabold text-[#E91E63]">
              Bottom Line
            </div>
            <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Von der Analyse zur Umsetzung. <span className="text-[#E91E63]">Nicht Dokumentation, sondern Wirkung.</span>
            </h3>
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
              <p>
                X10 versteht Prozessoptimierung nicht als theoretische Beratungsleistung, sondern als praktischen
                Veränderungsprozess. Wir verbinden organisatorisches Know-how, Digitalisierungskompetenz,
                Datenanalyse und branchenspezifische Erfahrung zu einem ganzheitlichen Ansatz.
              </p>
              <p className="text-[#0A192F] font-medium">
                Unser Ziel: Unternehmen leistungsfähiger machen, Ressourcen effizienter einsetzen und nachhaltige
                Wettbewerbsvorteile schaffen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#E91E63]/20 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#E91E63]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E91E63]" /> Lassen Sie uns starten
          </span>
          <h3 className="mt-5 font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Lassen Sie uns Ihre Potenziale sichtbar machen.
          </h3>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed">
            Jedes Unternehmen verfügt über ungenutzte Effizienzreserven. Die entscheidende Frage ist nicht, ob
            Potenziale vorhanden sind – sondern wie schnell sie erkannt und genutzt werden.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/#contact"
              data-testid="po-cta-bottom"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#E91E63] hover:text-white transition-all duration-300"
            >
              Beratungsgespräch vereinbaren
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#services"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Weitere Leistungen entdecken
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
